import { z } from 'zod';

/** API version of the contract. Bump on breaking changes. */
export const API_CONTRACT_VERSION = '2';

/* -------------------------------------------------------------------------- */
/* GET /api/health                                                            */
/* -------------------------------------------------------------------------- */

export const HealthResponseSchema = z.object({
  status: z.literal('ok'),
  timestamp: z.iso.datetime(),
  version: z.string(),
  environment: z.enum(['development', 'preview', 'production']),
});
export type HealthResponse = z.infer<typeof HealthResponseSchema>;

/* -------------------------------------------------------------------------- */
/* Error envelope — every non-2xx JSON response matches this shape.           */
/* -------------------------------------------------------------------------- */

export const ApiErrorSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.unknown().optional(),
    requestId: z.string().optional(),
  }),
});
export type ApiError = z.infer<typeof ApiErrorSchema>;

/* -------------------------------------------------------------------------- */
/* Shared primitives                                                          */
/* -------------------------------------------------------------------------- */

/** The six-hue flavor palette from the design system. Drives pseudonyms + tint. */
export const FLAVORS = ['citron', 'peach', 'berry', 'grape', 'lychee', 'melon'] as const;
export const FlavorSchema = z.enum(FLAVORS);
export type Flavor = z.infer<typeof FlavorSchema>;

/** e.g. "Anonymous Lychee" — a per-context pseudonym shown instead of a handle. */
export const PseudonymSchema = z.object({
  label: z.string().min(1).max(40),
  flavor: FlavorSchema,
  emoji: z.string().min(1).max(8),
});
export type Pseudonym = z.infer<typeof PseudonymSchema>;

export const IdSchema = z.string().min(8).max(64);

/** Latitude / longitude captured from the browser Geolocation API. */
export const CoordsSchema = z.object({
  lat: z.number().gte(-90).lte(90),
  lng: z.number().gte(-180).lte(180),
  /** Reported accuracy in metres, if the browser gave one. */
  accuracy: z.number().nonnegative().optional(),
  /** Compass heading in degrees (0 = north), if available. */
  heading: z.number().gte(0).lt(360).optional(),
});
export type Coords = z.infer<typeof CoordsSchema>;

export const CursorPageSchema = <T extends z.ZodTypeAny>(item: T) =>
  z.object({
    items: z.array(item),
    nextCursor: z.string().nullable(),
  });
